<template>
  <v-menu
    v-if="$vuetify.breakpoint.smAndUp"
    offset-y
    v-model="showPopup"
    :nudge-bottom="12"
    :nudge-left="360"
    min-width="412"
  >
    <template v-slot:activator="{}">
      <v-btn icon :loading="loading" @click="handleShowApps">
        <v-icon>mdi-apps</v-icon>
      </v-btn>
    </template>
    <v-card class="apps">
      <button
        :data-test-app="item.name"
        class="text-decoration-none apps__item"
        :class="{ 'apps__item--disable': !item.enabled }"
        @click="goToApp(item)"
        v-for="item in buttons"
        :key="item.id"
      >
        <img
          class="apps__item-image"
          :src="`/img/${item.icon}`"
          :alt="item.name"
        />
        <span class="apps__item-text pt-2">{{ item.name }}</span>
      </button>
    </v-card>
  </v-menu>
  <layout-popup v-else>
    <template slot="header">
      Change application
    </template>
    <template>
      <div class="mx-4">
        <v-progress-linear
          v-if="loading"
          active
          indeterminate
          absolute
          top
          color="primary accent-4"
        ></v-progress-linear>
        <button
          :data-test-app="item.name"
          class="text-decoration-none apps__item"
          :class="{ 'apps__item--disable': !item.enabled }"
          @click="goToApp(item)"
          v-for="item in buttons"
          :key="item.id"
        >
          <img
            class="apps__item-image"
            :src="`/img/${item.icon}`"
            :alt="item.name"
          />
          <span class="apps__item-text pt-2">{{ item.name }}</span>
        </button>
      </div>
    </template>
    <template slot="footer">
      <v-spacer />
      <v-btn data-test-app-select="close" text @click="$emit('close')">
        close
      </v-btn>
    </template>
  </layout-popup>
</template>

<script>
import LayoutPopup from '@/layouts/LayoutPopup';
import { useLazyQuery, useResult } from '@vue/apollo-composable';
import { onMounted, ref } from '@vue/composition-api';

import schemaGql from '@/api/schemas/item.graphql';
import useAuth from '@/api/auth';
import uniqby from 'lodash.uniqby';

export default {
  name: 'AppSelect',
  components: { LayoutPopup },
  setup(props, context) {
    const { user } = new useAuth();
    const variables = ref({ id: null, orderBy: 'NAME_DESC' });
    const { result, restart, load, loading, onResult } = useLazyQuery(
      schemaGql.userApps,
      variables,
      {
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'network-only'
      }
    );
    const showPopup = ref(false);

    const token = window.localStorage.getItem('refreshToken');
    const tokenId = window.localStorage.getItem('tokenId');

    const buttons = useResult(result, [], data => {
      const arr = data?.user.userProfiles
        .filter(item => Boolean(item.object))
        .map(item => item.object);

      return uniqby(arr, 'name');
    });

    onResult(() => {
      showPopup.value = true;
    });

    const goToApp = item => {
      showPopup.value = false;
      window.open(
        `${item.url}/from-app/?token=${token}&tokenId=${tokenId}`,
        '_blank'
      );
    };

    const handleShowApps = () => {
      if (showPopup.value) {
        showPopup.value = false;
        return;
      }
      variables.value.id = user.value.id;

      if (!result.value) {
        load();
      } else {
        restart();
      }
    };

    onMounted(async () => {
      if (context.root.$vuetify.breakpoint.xs) {
        handleShowApps();
      }
    });

    return {
      showPopup,
      buttons,
      handleShowApps,
      loading,
      token,
      tokenId,
      goToApp
    };
  }
};
</script>

<style lang="scss">
.apps {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-gap: 40px;
  padding: 25px;

  &__item {
    display: flex;
    flex-direction: column;
    align-items: center;

    @media (max-width: 600px) {
      width: 100%;
      flex-direction: row;
      margin-bottom: 18px;
      margin-top: 18px;
    }
  }

  &__item--disable {
    pointer-events: none;
    filter: grayscale(100);
    opacity: 0.5;
  }

  &__item-image {
    width: 40px;
    height: 40px;
  }

  &__item-text {
    color: #000;

    @media (max-width: 600px) {
      padding-left: 14px;
      font-size: 16px;
    }
  }
}

.theme--dark .apps__item-text {
  color: #fff;
}
</style>
